import { useMemo } from 'react';

import { Input } from '@/components/shadcn/ui/input';
import { Label } from '@/components/shadcn/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/shadcn/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/ui/select';
import {
  CHART_PERIOD_RELATIVE,
  CHART_TERM_TYPE,
  CHART_TERM_UNIT,
} from '@/entities/chart/model/constants';
import {
  createDefaultTemplateFixedDateTerm,
  createDefaultTemplatePeriodicDateTerm,
  getTemplatePeriodicTermMaxValue,
  type TemplateDateTermInput,
} from '@/entities/dashboard-template';
import { DateRangePicker } from '@/shared/ui/date-range-picker';
import { getFieldError } from '@/shared/ui/form/get-field-error';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';

const TERM_TYPE_OPTIONS: { value: CHART_TERM_TYPE; label: string }[] = [
  { value: CHART_TERM_TYPE.PERIODIC, label: 'Periodic' },
  { value: CHART_TERM_TYPE.FIXED, label: 'Fixed' },
];

const TERM_UNIT_OPTIONS: {
  value:
    | CHART_TERM_UNIT.DAY
    | CHART_TERM_UNIT.WEEK
    | CHART_TERM_UNIT.MONTH
    | CHART_TERM_UNIT.QUARTER;
  label: string;
}[] = [
  { value: CHART_TERM_UNIT.DAY, label: 'Day' },
  { value: CHART_TERM_UNIT.WEEK, label: 'Week' },
  { value: CHART_TERM_UNIT.MONTH, label: 'Month' },
  { value: CHART_TERM_UNIT.QUARTER, label: 'Quarter' },
];

const TERM_RELATIVE_OPTIONS: { value: CHART_PERIOD_RELATIVE; label: string }[] =
  [
    { value: CHART_PERIOD_RELATIVE.INCLUDE, label: 'Including' },
    { value: CHART_PERIOD_RELATIVE.EXCLUDE, label: 'Excluding' },
  ];

type Props = {
  value: TemplateDateTermInput;
  onChange: (value: TemplateDateTermInput) => void;
  errors?: unknown[];
  required?: boolean;
  label?: string;
};

export const TemplateDateTermField = ({
  value,
  onChange,
  errors,
  required = true,
  label = 'Date Range',
}: Props) => {
  const errorMessage = useMemo(
    () => getFieldError(errors?.[0]),
    [errors],
  );

  const handleTypeChange = (nextTypeString: string) => {
    const nextType = Number(nextTypeString) as CHART_TERM_TYPE;
    if (nextType === CHART_TERM_TYPE.PERIODIC) {
      onChange(
        value.type === CHART_TERM_TYPE.PERIODIC
          ? value
          : createDefaultTemplatePeriodicDateTerm(),
      );
      return;
    }

    onChange(
      value.type === CHART_TERM_TYPE.FIXED
        ? value
        : createDefaultTemplateFixedDateTerm(),
    );
  };

  const fixedDateDisplayText =
    value.type === CHART_TERM_TYPE.FIXED &&
    value.startDate &&
    value.endDate
      ? `${getTimezoneDayjs(value.startDate).format(DATE_FORMAT.readable)} - ${getTimezoneDayjs(value.endDate).format(DATE_FORMAT.readable)}`
      : 'Pick a date range';

  return (
    <div className="grid w-full items-center gap-2">
      <Label>
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
      </Label>

      <RadioGroup
        value={String(value.type)}
        onValueChange={handleTypeChange}
        className="flex items-center gap-4"
      >
        {TERM_TYPE_OPTIONS.map((option) => (
          <div key={option.value} className="flex items-center gap-2">
            <RadioGroupItem
              value={String(option.value)}
              id={`template-date-term-type-${option.value}`}
            />
            <Label
              htmlFor={`template-date-term-type-${option.value}`}
              className="cursor-pointer"
            >
              {option.label}
            </Label>
          </div>
        ))}
      </RadioGroup>

      {value.type === CHART_TERM_TYPE.FIXED && (
        <DateRangePicker
          showPresets={false}
          value={
            value.startDate && value.endDate
              ? {
                  type: 'dateRange',
                  dateRange: {
                    from: getTimezoneDayjs(value.startDate),
                    to: getTimezoneDayjs(value.endDate),
                  },
                }
              : undefined
          }
          placeholder="Pick a date range"
          onChange={(nextValue) => {
            if (nextValue.type !== 'dateRange') {
              return;
            }

            onChange({
              ...value,
              startDate: getTimezoneDayjs(
                nextValue.dateRange.from.toJSON(),
              ).format(DATE_FORMAT.base),
              endDate: getTimezoneDayjs(nextValue.dateRange.to.toJSON()).format(
                DATE_FORMAT.base,
              ),
            });
          }}
          trigger={
            <button
              type="button"
              className="flex h-9 w-full items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-left text-sm shadow-xs transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              <span
                className={
                  value.startDate
                    ? 'text-foreground'
                    : 'text-muted-foreground'
                }
              >
                {fixedDateDisplayText}
              </span>
            </button>
          }
          minDate={undefined}
          maxDate={undefined}
          presetNames={[]}
        />
      )}

      {value.type === CHART_TERM_TYPE.PERIODIC && (
        <div className="grid gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Last</span>
            <Input
              type="number"
              className="w-24"
              min={1}
              max={getTemplatePeriodicTermMaxValue(value.unit)}
              value={value.value}
              onChange={(event) => {
                const nextValue = Number(event.target.value);
                onChange({
                  ...value,
                  value: Number.isNaN(nextValue) ? 0 : nextValue,
                });
              }}
            />
            <Select
              value={String(value.unit)}
              onValueChange={(nextUnitString) => {
                const nextUnit = Number(nextUnitString) as typeof value.unit;
                onChange({
                  ...value,
                  unit: nextUnit,
                  value: Math.min(
                    value.value,
                    getTemplatePeriodicTermMaxValue(nextUnit),
                  ),
                });
              }}
            >
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TERM_UNIT_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={String(option.value)}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Select
              value={String(value.relative)}
              onValueChange={(nextRelativeString) => {
                onChange({
                  ...value,
                  relative: Number(nextRelativeString) as CHART_PERIOD_RELATIVE,
                });
              }}
            >
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TERM_RELATIVE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={String(option.value)}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">
              {value.unit === CHART_TERM_UNIT.DAY && 'today'}
              {value.unit === CHART_TERM_UNIT.WEEK && 'this week'}
              {value.unit === CHART_TERM_UNIT.MONTH && 'this month'}
              {value.unit === CHART_TERM_UNIT.QUARTER && 'this quarter'}
            </span>
          </div>
        </div>
      )}

      {errorMessage && (
        <span className="text-destructive text-sm">{errorMessage}</span>
      )}
    </div>
  );
};
